/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useRef } from 'react';

import {
  createChatChannel,
  isRequestAborted,
  resolveChatApiErrorMessage,
  resolveInlineOrLrrResponse,
  streamChatResponse,
} from './chatApi';
import { hasGenerationSnapshot, parseLrrPollSnapshot } from './lrrProgress';
import { resolveChatMessageRef } from './messageRefUtils';
import { parseFollowUpQuestions } from './parseFollowUpQuestions';

export const COMMAND_INTELLIGENCE_LRR_POLL_OPTIONS = {
  intervalMs: 1500,
  maxAttempts: 120,
};

export const useCommandIntelligenceStreamingGeneration = ({
  entityRef,
  intl,
  preset,
  mountedRef,
  channelRefRef,
  makeId,
  setMessages,
  setIsSending,
  isSending,
  wrapUserMessage,
  buildAttachedContextPayload,
}) => {
  const abortRef = useRef(null);
  const streamingIdRef = useRef(null);

  const clearStreamingState = useCallback(() => {
    streamingIdRef.current = null;
    setIsSending(false);
  }, [setIsSending]);

  const finalizeStoppedGeneration = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }

    const streamingCrn = streamingIdRef.current;

    if (!streamingCrn) {
      setIsSending(false);

      return;
    }

    setMessages(previousMessages => {
      const streamingMessage = previousMessages.find(message => message.crn === streamingCrn);
      const generationSnapshot = streamingMessage?.metadata?.generationSnapshot ?? null;
      const withoutStreaming = previousMessages.filter(message => !message.isStreaming);

      return [
        ...withoutStreaming,
        {
          crn: makeId('stopped'),
          text: intl.formatMessage({ id: 'command_intelligence_response_stopped' }),
          role: 'system',
          sender: 'system',
          timestamp: new Date().toISOString(),
          chatChannelRef: channelRefRef.current || '',
          ...(hasGenerationSnapshot(generationSnapshot) ? { metadata: { generationSnapshot } } : {}),
        },
      ];
    });

    clearStreamingState();
  }, [channelRefRef, clearStreamingState, intl, makeId, mountedRef, setIsSending, setMessages]);

  const handleStopGeneration = useCallback(() => {
    if (!isSending || !abortRef.current) {
      return;
    }

    abortRef.current.abort();
    finalizeStoppedGeneration();
  }, [finalizeStoppedGeneration, isSending]);

  const updateStreamingProgress = useCallback(
    lrr => {
      if (!mountedRef.current || !streamingIdRef.current) {
        return;
      }

      const snapshot = parseLrrPollSnapshot(lrr);
      const streamingCrn = streamingIdRef.current;

      setMessages(previousMessages =>
        previousMessages.map(message =>
          message.crn === streamingCrn
            ? {
                ...message,
                metadata: {
                  ...(message.metadata ?? {}),
                  generationSnapshot: snapshot,
                },
              }
            : message
        )
      );
    },
    [mountedRef, setMessages]
  );

  const finalizeAssistantMessage = useCallback(
    (rawResponseText, conversationRef, extraMetadata = {}, isRunError = false) => {
      if (!mountedRef.current || !streamingIdRef.current) {
        return;
      }

      const raw = typeof rawResponseText === 'string' ? rawResponseText : String(rawResponseText ?? '');
      const { displayText, followUpQuestions } = parseFollowUpQuestions(raw);

      if (conversationRef) {
        channelRefRef.current = conversationRef;
      }

      const mergedMeta = extraMetadata ?? {};

      const latencyMs =
        typeof mergedMeta.latencyMs === 'number'
          ? mergedMeta.latencyMs
          : typeof mergedMeta.serverDurationMs === 'number'
          ? mergedMeta.serverDurationMs
          : undefined;

      setMessages(previousMessages => {
        const streamingMessage = previousMessages.find(message => message.crn === streamingIdRef.current);
        const generationSnapshot =
          streamingMessage?.metadata?.generationSnapshot ?? mergedMeta.generationSnapshot ?? null;
        const withoutStreaming = previousMessages.filter(message => !message.isStreaming);
        const serverRef =
          resolveChatMessageRef(mergedMeta.messageRef) || resolveChatMessageRef(mergedMeta.crn) || '';
        const crn = serverRef || streamingIdRef.current || makeId('assistant');

        return [
          ...withoutStreaming,
          {
            crn,
            text: displayText || '',
            role: 'assistant',
            sender: 'assistant',
            timestamp: new Date().toISOString(),
            chatChannelRef: conversationRef || channelRefRef.current || '',
            isStreaming: false,
            ...(isRunError ? { isError: true } : {}),
            followUpQuestions,
            metadata: {
              followUpQuestions,
              ...mergedMeta,
              ...(hasGenerationSnapshot(generationSnapshot) ? { generationSnapshot } : {}),
              ...(latencyMs !== undefined ? { latencyMs } : {}),
            },
          },
        ];
      });

      clearStreamingState();
    },
    [channelRefRef, clearStreamingState, makeId, mountedRef, setMessages]
  );

  const runAssistantTurn = useCallback(
    async ({ userPrompt, seedMessages, fetchResponse, resolveConversationRef }) => {
      abortRef.current?.abort();
      const controller = new AbortController();

      abortRef.current = controller;

      const streamingCrn = makeId('streaming');

      streamingIdRef.current = streamingCrn;

      const seedResult = seedMessages(userPrompt, streamingCrn);

      setMessages(seedResult);

      setIsSending(true);

      try {
        const response = await fetchResponse({
          userMessage: wrapUserMessage(userPrompt),
          attachedContext: buildAttachedContextPayload(),
          signal: controller.signal,
        });

        if (!mountedRef.current) {
          return;
        }

        if (controller.signal.aborted) {
          finalizeStoppedGeneration();

          return;
        }

        if (response === null) {
          // http returns null when the JWT is expired (pre-fetch) or 401 without suppressUnauthenticatedOn401
          // eslint-disable-next-line no-throw-literal -- intentional; not Error('401')
          throw null;
        }

        if (response === undefined) {
          // http returns undefined after fetch/network failures (aborts are handled above)
          throw new Error('Network');
        }

        const resolved = await resolveInlineOrLrrResponse({
          entityRef,
          response,
          signal: controller.signal,
          pollOptions: {
            ...COMMAND_INTELLIGENCE_LRR_POLL_OPTIONS,
            onPoll: updateStreamingProgress,
          },
        });

        finalizeAssistantMessage(
          resolved.responseText ?? '',
          resolveConversationRef(response),
          resolved.metadata,
          resolved.isRunError
        );
      } catch (error) {
        if (!mountedRef.current) {
          return;
        }

        if (isRequestAborted(error, controller.signal)) {
          finalizeStoppedGeneration();

          return;
        }

        const friendly = await resolveChatApiErrorMessage(error, intl, userPrompt);

        setMessages(previousMessages => [
          ...previousMessages.filter(message => !message.isStreaming),
          {
            crn: makeId('error'),
            text: friendly,
            role: 'assistant',
            sender: 'assistant',
            timestamp: new Date().toISOString(),
            chatChannelRef: channelRefRef.current || '',
            isError: true,
          },
        ]);
        clearStreamingState();
      }
    },
    [
      buildAttachedContextPayload,
      clearStreamingState,
      entityRef,
      finalizeAssistantMessage,
      finalizeStoppedGeneration,
      intl,
      makeId,
      mountedRef,
      setIsSending,
      setMessages,
      updateStreamingProgress,
      wrapUserMessage,
      channelRefRef,
    ]
  );

  const sendFirstMessage = useCallback(
    async userPrompt => {
      if (!entityRef) {
        return;
      }

      await runAssistantTurn({
        userPrompt,
        seedMessages: (prompt, streamingCrn) => [
          {
            crn: makeId('user-initial'),
            text: prompt,
            role: 'user',
            sender: 'user',
            timestamp: new Date().toISOString(),
            chatChannelRef: '',
          },
          {
            crn: streamingCrn,
            text: '',
            role: 'assistant',
            sender: 'assistant',
            timestamp: new Date().toISOString(),
            chatChannelRef: '',
            isStreaming: true,
          },
        ],
        fetchResponse: async ({ userMessage, attachedContext, signal }) => {
          const parsed = await createChatChannel(
            entityRef,
            {
              name: intl.formatMessage({ id: preset.channelNameId }),
              chatType: 'AI',
              chatMode: 'custom-agent',
              agentRef: preset.agentRef,
              messages: [{ text: userMessage, role: 'user' }],
              ...(Object.keys(attachedContext).length > 0 ? { attachedContext } : {}),
            },
            { signal }
          );

          if (parsed === null) {
            // eslint-disable-next-line no-throw-literal -- intentional; not Error('401')
            throw null;
          }

          if (parsed === undefined) {
            throw new Error('Network');
          }

          if (!parsed?.chatRef) {
            throw parsed;
          }

          channelRefRef.current = parsed.chatRef;

          return parsed;
        },
        resolveConversationRef: response => response.chatRef,
      });
    },
    [channelRefRef, entityRef, intl, makeId, preset.agentRef, preset.channelNameId, runAssistantTurn]
  );

  const sendFollowUpMessage = useCallback(
    async userPrompt => {
      if (!entityRef || !channelRefRef.current) {
        return;
      }

      const conversationRef = channelRefRef.current;

      await runAssistantTurn({
        userPrompt,
        seedMessages: (prompt, streamingCrn) => previousMessages =>
          [
            ...previousMessages,
            {
              crn: makeId('user'),
              text: prompt,
              role: 'user',
              sender: 'user',
              timestamp: new Date().toISOString(),
              chatChannelRef: conversationRef,
            },
            {
              crn: streamingCrn,
              text: '',
              role: 'assistant',
              sender: 'assistant',
              timestamp: new Date().toISOString(),
              chatChannelRef: conversationRef,
              isStreaming: true,
            },
          ],
        fetchResponse: async ({ userMessage, attachedContext, signal }) =>
          streamChatResponse(
            entityRef,
            conversationRef,
            {
              text: userMessage,
              role: 'user',
              agentRef: preset.agentRef,
              ...(Object.keys(attachedContext).length > 0 ? { attachedContext } : {}),
            },
            { signal }
          ),
        resolveConversationRef: response => response?.chatChannelRef ?? conversationRef,
      });
    },
    [channelRefRef, entityRef, makeId, preset.agentRef, runAssistantTurn]
  );

  return {
    abortRef,
    streamingIdRef,
    handleStopGeneration,
    sendFirstMessage,
    sendFollowUpMessage,
  };
};
